import { _ } from "../../../../core/polyglot/polyglot.js";
import { formatFullWithNoDecimalsNorCurrency } from "../../../../core/helpers/currency-formatting-util.js";
import { Widget } from "@foretagsplatsen/widgetjs";

/**
 * `AgedAccountsSummaryTableWidget` displays an AgedAccountsSummary
 * as a table. This table is the legend of the DoughnutChartWidget.
 *
 * @param {AgedAccountsSummary} summary - The summary model used to render the
 * corresponding widgets.
 * @param {string[]} colors - The colors needed for rendering each group. Its length must
 * be the same as the summary groups.
 * @param {func} [mouseOverCallback] - Callback to execute when the mouse hovers on and off
 * an item row. Takes the item as parameter (or undefined when the mouse leaves the area).
 */
export default class AgedAccountsSummaryTableWidget extends Widget {
	constructor({ summary, colors, mouseOverCallback = () => {} }) {
		super(...arguments);

		this._summary = summary;
		this._colors = colors;
		this._mouseOverCallback = mouseOverCallback;

		// Defaults to none of the items to be highlighted.
		this._highlightedGroup = undefined;
	}

	renderContentOn(html) {
		html.table(
			{
				klass: "aged-accounts-table",
				"data-test": "aged-accounts-table",
				mouseleave: () => this._mouseOverCallback(undefined),
			},
			html.thead(this._renderHeadingRowOn.bind(this)),
			html.tbody([
				this._renderGroupRowsOn.bind(this),
				this._renderSummaryRowOn.bind(this),
			]),
		);
	}

	highlightGroup(group) {
		if (this._highlightedGroup === group) {
			return;
		}

		this._highlightedGroup = group;
		this.update();
	}

	_isHighlighted(group) {
		return group && this._highlightedGroup === group;
	}

	_renderHeadingRowOn(html) {
		html.tr(
			html.th(_("Aged")),
			html.th(_("Amount")),
			html.th(_("Percentage")),
		);
	}

	_renderGroupRowsOn(html) {
		this._summary.getGroups().forEach((group, index) => {
			const color = this._colors[index];

			html.tr(
				{
					klass: `${this._isHighlighted(group) ? "highlighted" : ""}`,
					"data-test": "aged-accounts-table-row",
					mouseenter: () => this._mouseOverCallback(group),
				},
				this._groupRowRenderer(group, color),
			);
		});
	}

	_groupRowRenderer(group, color) {
		return (html) => [
			html.td(this._labelRenderer(group, color)),
			html.td(
				{ "data-test": "aged-accounts-table-row-value" },
				this._valueRenderer(group.getValue()),
			),
			html.td(`${group.getPercentage()}%`),
		];
	}

	_renderSummaryRowOn(html) {
		return html.tr(
			{ klass: "aged-summary" },
			html.td(_("Total")),
			html.td(this._valueRenderer(this._summary.getTotal())),
			html.td("100%"),
		);
	}

	_labelRenderer(group, color) {
		return (html) =>
			html.div(
				{ klass: "aged-summary-with-legend" },
				html.div({
					klass: [
						"aged-summary-legend",
						`legend-${group.getLegend()}`,
					],
					"data-test": "aged-summary-legend",
					style: `background-color: ${color}`,
				}),
				group.getLabel(),
			);
	}

	_valueRenderer(number) {
		return formatFullWithNoDecimalsNorCurrency(number);
	}
}
